import { useEffect, useMemo, useState } from "react";
import { ArrowRight, CalendarCheck } from "lucide-react";
import { PageHeader, StatCard, StatusPill } from "../components/Primitives";
import { api, errorText, formatDate, todayISO } from "../lib/api";

export default function Attendance() {
  const [records, setRecords] = useState([]);
  const [students, setStudents] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [summary, setSummary] = useState({});
  const [error, setError] = useState("");
  const [date, setDate] = useState(todayISO());

  const load = async (forDate = date) => {
    try {
      const [recordsRes, studentsRes, shiftsRes, summaryRes] = await Promise.all([
        api.get("/attendance", { params: { date_from: forDate, date_to: forDate } }),
        api.get("/students"),
        api.get("/shifts"),
        api.get("/attendance/summary"),
      ]);
      setRecords(recordsRes.data.data);
      setStudents(studentsRes.data.data);
      setShifts(shiftsRes.data.data);
      setSummary(summaryRes.data.data);
    } catch (err) {
      setError(errorText(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const recordedBy = useMemo(() => new Set(records.map((record) => record.student_id)), [records]);

  const mark = async (studentId, status) => {
    try {
      await api.post("/attendance", { student_id: studentId, status, date });
      load(date);
    } catch (err) {
      setError(errorText(err));
    }
  };

  const checkOut = async (studentId) => {
    try {
      await api.post("/attendance/check-out", { student_id: studentId });
      load(date);
    } catch (err) {
      setError(errorText(err));
    }
  };

  const isToday = date === todayISO();

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="DAILY TRACKING"
        title="Attendance"
        description="Mark present, absent or late — see today's roll-up live."
        action={
          <div className="toolbar-actions">
            <input type="date" value={date} onChange={(event) => { setDate(event.target.value); load(event.target.value); }} data-testid="attendance-date" />
          </div>
        }
      />
      {error ? <div className="error-message">{error}</div> : null}
      <div className="stat-grid">
        <StatCard label="Present today" value={summary.present ?? 0} detail="Marked present" icon={<CalendarCheck size={18} />} tone="green" />
        <StatCard label="Absent today" value={summary.absent ?? 0} detail="Marked absent" tone="orange" icon={<CalendarCheck size={18} />} />
        <StatCard label="Late" value={summary.late ?? 0} detail="Checked in late" icon={<CalendarCheck size={18} />} />
        <StatCard label="Total students" value={summary.total_students ?? students.length} detail="Active roster" tone="dark" icon={<CalendarCheck size={18} />} />
      </div>

      <section className="content-card table-card">
        <div className="table-toolbar">
          <div>
            <span className="card-kicker">ROSTER</span>
            <h2>{students.length} students</h2>
          </div>
        </div>
        <div className="table-wrap">
          <table data-testid="attendance-table">
            <thead>
              <tr><th>Student</th><th>Shift</th><th>Status</th><th>Check-in</th><th>Check-out</th><th>Mark</th></tr>
            </thead>
            <tbody>
              {students.map((student) => {
                const record = records.find((item) => item.student_id === student.id);
                const hasCheckedIn = !!record;
                const hasCheckedOut = !!record?.check_out;
                return (
                  <tr key={student.id} data-testid={`attendance-row-${student.student_id}`}>
                    <td>
                      <div className="person-cell">
                        <span className="table-avatar">{student.name?.charAt(0)}</span>
                        <div><strong>{student.name}</strong><small>{student.student_id}</small></div>
                      </div>
                    </td>
                    <td>{shifts.find((shift) => shift.id === student.shift_id)?.name || "—"}</td>
                    <td>{record ? <StatusPill status={record.status} /> : <span style={{ color: "#94a3b8" }}>Not marked</span>}</td>
                    <td>{record?.check_in ? new Date(record.check_in).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td>{record?.check_out ? new Date(record.check_out).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td>
                      <div className="action-row">
                        {!hasCheckedIn ? (
                          <button className="button button-primary button-small" onClick={() => mark(student.id, "PRESENT")} data-testid={`check-in-${student.id}`} disabled={!isToday}>Check in</button>
                        ) : !hasCheckedOut ? (
                          <button className="button button-outline button-small" onClick={() => checkOut(student.id)} data-testid={`check-out-${student.id}`} disabled={!isToday}>Check out</button>
                        ) : (
                          <span style={{ color: "#16a34a", fontSize: 12, fontWeight: 600 }}>Done</span>
                        )}
                        <button className="button button-outline button-small" onClick={() => mark(student.id, "LATE")} data-testid={`mark-late-${student.id}`}>Late</button>
                        <button className="button button-outline button-small" onClick={() => mark(student.id, "ABSENT")} data-testid={`mark-absent-${student.id}`}>Absent</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
